import React, { useState, useEffect, useRef } from 'react';
import api from "../../api/axios";
import './Settings.css';
// import { toast } from "react-toastify";

// Helper for avatars
const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

const WorkSpaceManagement = () => {
    // --- VISIBILITY STATES ---
    const [activeModal, setActiveModal] = useState(null);
    const [expandedWS, setExpandedWS] = useState(null);
    const [activeWSIndex, setActiveWSIndex] = useState(null);
    const [showUserMenu, setShowUserMenu] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    // ✅ PAGINATION STATE (PRESERVED)
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 10;

    // UI Popup States
    const [showNamePopup, setShowNamePopup] = useState(false);
    const [showEmailPopup, setShowEmailPopup] = useState(false);

    // ✅ TOAST STATE
    const [toastState, setToastState] = useState({
        show: false,
        message: "",
        type: "success"
    });
    const toastTimeoutRef = useRef(null);

    // ✅ PERMISSIONS & DYNAMIC DATA STATES
    const [canWrite, setCanWrite] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [isInviting, setIsInviting] = useState(false);
    const [agentsModal, setAgentsModal] = useState(false);
    const [userAgents, setUserAgents] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [agents, setAgents] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);

    // ✅ NEW SECURITY LAYER STATE
    const [agentPermissions, setAgentPermissions] = useState({});

    const [workspaces, setWorkspaces] = useState([
        { id: 1, name: "Finance", industry: "SaaS", department: "HR", members: "0", userList: [] }
    ]);

    const [wsForm, setWsForm] = useState({ name: "", industry: "SaaS", department: "" });
    const [userForm, setUserForm] = useState({ name: "", email: "", role: "", status: "Active" });
    const [inviteEmail, setInviteEmail] = useState("");
    const [tempEmails, setTempEmails] = useState([]);

    // ✅ FETCH PERMISSIONS & ROLES ON MOUNT (100% UNTOUCHED)
    useEffect(() => {
        const initializeWorkspace = async () => {
            try {
                const permRes = await api.get("/user-roles/me");
                const role = permRes.data;

                if (role.isAdmin === true) {
                    setCanWrite(true);
                } else if (role && !role.noRoleDefined) {
                    const writeStr = role.write_access || "";
                    const bothStr = role.both_access || "";
                    const allowed = writeStr.includes("Workspace Management") || bothStr.includes("Workspace Management");
                    setCanWrite(allowed);
                } else {
                    setCanWrite(true);
                }

                const tiersRes = await api.get("/user-roles");
                setAvailableRoles(tiersRes.data);

                const wsRes = await api.get("/workspace/me");
                if (wsRes.data) {
                    setWorkspaces([{
                        id: wsRes.data.id,
                        name: wsRes.data.workspace_name,
                        industry: wsRes.data.industry || "SaaS",
                        department: wsRes.data.department || "",
                        members: "0",
                        userList: []
                    }]);
                }

                const usersRes = await api.get("/workspace/users");
                const agentsRes = await api.get("/agents");
                setAgents(agentsRes.data);

                setWorkspaces(prev => {
                    const newWS = [...prev];
                    if (newWS[0]) {
                        newWS[0].userList = usersRes.data.map(user => ({
                            name: user.name,
                            email: user.email,
                            role: user.role_name || "No Role",
                            role_id: user.role_id,
                            status: user.is_active ? "Active" : "Inactive"
                        }));
                        newWS[0].members = usersRes.data.length.toString();
                    }
                    return newWS;
                });

            } catch (err) {
                console.error("Workspace Initialization Error:", err);
                setCanWrite(true);
            }
        };

        initializeWorkspace();

        const handleClickOutside = () => setShowUserMenu(null);
        window.addEventListener('click', handleClickOutside);

        return () => {
            if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
            window.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const showToast = (message, type = "success") => {
        if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
        setToastState({ show: true, message, type });
        toastTimeoutRef.current = setTimeout(() => {
            setToastState({ show: false, message: "", type: "success" });
        }, 3000);
    };

   const openAddUserModal = async (idx) => {
        try {
            // ✅ CACHE BUSTER: Force fetch the absolute latest agents from DB!
            const freshAgentsRes = await api.get(`/agents?t=${new Date().getTime()}`);
            setAgents(freshAgentsRes.data);

            const tiersRes = await api.get("/user-roles");
            setAvailableRoles(tiersRes.data);
            setActiveWSIndex(idx);
            setUserForm({ name: "", email: "", role: "", status: "Active" });
            setSelectedAgents([]);
            setAgentPermissions({});
            setActiveModal("addUser");
        } catch (err) {
            setActiveModal("addUser");
        }
    };

    const industryList = [
        "SaaS", "FinTech", "Artificial Intelligence", "Cybersecurity", "HealthTech", "E-commerce", "Clean Energy", "EdTech",
        "Blockchain & Web3", "Cloud Computing", "Logistics", "BioTech", "Digital Marketing", "Venture Capital",
        "InsurTech", "Real Estate", "Autonomous Systems", "SpaceTech", "AgriTech", "Entertainment & Media"
    ];

    const handleAddEmail = () => {
        if (inviteEmail && !tempEmails.includes(inviteEmail)) {
            setTempEmails([...tempEmails, inviteEmail]);
            setInviteEmail("");
        }
    };

    const handleCreateWorkspace = () => {
        if (!canWrite) return;
        const newWS = {
            id: Date.now(),
            name: wsForm.name || "New Workspace",
            industry: wsForm.industry,
            department: wsForm.department || "General",
            members: tempEmails.length.toString(),
            userList: tempEmails.map(email => ({
                name: email.split('@')[0], email: email, role: "Member", status: "Active"
            }))
        };
        setWorkspaces([...workspaces, newWS]);
        setActiveModal(null);
        setTempEmails([]);
        setWsForm({ name: "", industry: "SaaS", department: "" });
    };

    const handleUpdateWorkspace = async () => {
        if (!canWrite) return;
        try {
            await api.put("/workspace/update", { name: wsForm.name, industry: wsForm.industry, department: wsForm.department });
            const updated = [...workspaces];
            updated[activeWSIndex] = { ...updated[activeWSIndex], name: wsForm.name, industry: wsForm.industry, department: wsForm.department };
            setWorkspaces(updated);
            setActiveModal(null);
        } catch (err) {
            showToast("Failed to update workspace", "error");
        }
    };

    const handleAddNewUser = async () => {
        if (!canWrite || isInviting) return;
        if (!userForm.name || !userForm.email || !userForm.role) return showToast(
            "Please fill name, email and select a role",
            "error"
        );

        try {
            setIsInviting(true);
            const payload = {
                name: userForm.name, email: userForm.email, role_id: userForm.role,
                status: userForm.status, agents: selectedAgents, agentPerms: agentPermissions
            };
            const res = await api.post("/workspace/invite-user", payload);
            showToast(res.data.message, "success");

            const usersRes = await api.get("/workspace/users");
            const updated = [...workspaces];
            if (updated[activeWSIndex]) {
                updated[activeWSIndex].userList = usersRes.data.map(user => ({
                    name: user.name, email: user.email, role: user.role_name || "No Role",
                    role_id: user.role_id, status: user.is_active ? "Active" : "Inactive"
                }));
                updated[activeWSIndex].members = usersRes.data.length.toString();
            }
            setWorkspaces(updated);
            setActiveModal(null);
            setUserForm({ name: "", email: "", role: "", status: "Active" });
            setSelectedAgents([]);
            setAgentPermissions({});
        } catch (err) {
            showToast(
                err.response?.data?.message || "Failed to send invitation",
                "error"
            );
        } finally {
            setIsInviting(false);
        }
    };

    const handleUpdateUser = async () => {
        try {
            await api.put("/workspace/update-user", {
                email: userForm.email, name: userForm.name, status: userForm.status,
                role_id: userForm.role_id, agents: selectedAgents, agentPerms: agentPermissions
            });
            showToast("User updated successfully", "success");
        } catch (err) {
            return showToast(
                err.response?.data?.message || "Failed to update user",
                "error"
            );
        }

        try {
            const usersRes = await api.get("/workspace/users");
            const updated = [...workspaces];
            if (updated[activeWSIndex]) {
                updated[activeWSIndex].userList = usersRes.data.map(user => ({
                    name: user.name, email: user.email, role: user.role_name || "No Role",
                    role_id: user.role_id, status: user.is_active ? "Active" : "Inactive"
                }));
            }
            setWorkspaces(updated);
            setActiveModal(null);
        } catch (err) {
            console.error("User list refresh failed", err);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            const { email, wsIdx } = userToDelete;

            await api.delete("/workspace/delete-user", {
                data: { email }
            });

            const usersRes = await api.get("/workspace/users");

            const updated = [...workspaces];

            if (updated[wsIdx]) {
                updated[wsIdx].userList = usersRes.data.map(user => ({
                    name: user.name,
                    email: user.email,
                    role: user.role_name || "No Role",
                    role_id: user.role_id,
                    status: user.is_active ? "Active" : "Inactive"
                }));

                updated[wsIdx].members = usersRes.data.length.toString();
            }

            setWorkspaces(updated);

            showToast(
                "User deleted successfully",
                "success"
            );

            const totalRemaining = updated[wsIdx].userList.length;

            if (
                currentPage > Math.ceil(totalRemaining / usersPerPage) &&
                currentPage > 1
            ) {
                setCurrentPage(currentPage - 1);
            }

            setShowDeleteModal(false);
            setUserToDelete(null);

        } catch (err) {
            showToast("Failed to delete user", "error");
        }
    };

   const openAgentAccess = async (user) => {
        try {
            // ✅ FIX: Added cache-buster so it always fetches fresh data from DB!
            const res = await api.get(`/workspace/user-agents/${user.email}?t=${new Date().getTime()}`);
            setUserAgents(res.data || []);
            setSelectedUser(user);
            setAgentsModal(true);
        } catch (err) {
            setUserAgents([]);
            setSelectedUser(user);
            setAgentsModal(true);
        }
    };
    const dropdownStyle = {
        backgroundImage: `url(${process.env.PUBLIC_URL + '/images/triarrow.png'})`, backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 0.75rem center', backgroundSize: '14px 10px'
    };

    const popupStyle = {
        position: 'absolute', bottom: '-32px', left: '5px', backgroundColor: '#1a73e8', color: '#fff',
        padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '500', zIndex: 9999,
        whiteSpace: 'nowrap', boxShadow: '0 4px 10px rgba(0,0,0,0.2)', pointerEvents: 'none'
    };

    return (
        <div className="wm-container text-start w-100">
            {toastState.show && (
                <div className={`notification-toast ${toastState.type === "success" ? "toast-success" : "toast-error"}`}>
                    {toastState.message}
                </div>
            )}

            {/* ── MAIN RENDER - EXTREMELY COMPACT & FULL WIDTH ── */}
            <div className="wm-content w-100">
                {workspaces.map((ws, idx) => {

                    // PAGINATION LOGIC
                    const indexOfLastUser = currentPage * usersPerPage;
                    const indexOfFirstUser = indexOfLastUser - usersPerPage;
                    const currentUsers = ws.userList.slice(indexOfFirstUser, indexOfLastUser);
                    const totalPages = Math.ceil(ws.userList.length / usersPerPage);

                    // Dynamic Box Initial Generator
                    const boxInitials = ws.department ? ws.department.substring(0, 2).toUpperCase() : "WS";

                    return (
                        <div key={ws.id} className="w-100">
                            {/* ── 1. COMPACT WORKSPACE MANAGEMENT CARD ── */}
                            <div className="card border-0 shadow-sm rounded-3 mb-3 px-4 py-2 w-100">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-grid text-primary" style={{ fontSize: "15px" }}></i>
                                        <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "16px" }}>Workspace Management</h6>
                                    </div>
                                    {canWrite && (
                                        <i className="bi bi-pencil-square text-muted cursor-pointer hover-primary"
                                            style={{ fontSize: "15px" }}
                                            onClick={() => { setWsForm({ ...ws }); setActiveWSIndex(idx); setActiveModal('editWS'); }}>
                                        </i>
                                    )}
                                </div>
                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="wm-dept-iconbox">
                                            {boxInitials}
                                        </div>
                                        <div className="fw-bold text-dark me-1" style={{ fontSize: "16px" }}>{ws.name}</div>
                                        <span className="text-secondary" style={{ fontSize: "13px" }}>Company</span>
                                    </div>
                                    <div className="d-flex gap-5 text-center pe-2">
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="text-muted mb-1" style={{ fontSize: "11px" }}>Members</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: "15px" }}>{ws.members}</div>
                                        </div>
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="text-muted mb-1" style={{ fontSize: "11px" }}>Industry</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: "15px" }}>{ws.industry}</div>
                                        </div>
                                        <div className="d-flex flex-column align-items-center">
                                            <div className="text-muted mb-1" style={{ fontSize: "11px" }}>Department</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: "15px" }}>{ws.department}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── 2. COMPACT USER MANAGEMENT CARD & TABLE ── */}
                            <div className="card border-0 shadow-sm rounded-3 p-3 w-100">
                                <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "16px" }}>User Management</h6>
                                    <div className="d-flex align-items-center gap-3">
                                        <span className="wm-badge-total px-3 py-1">{ws.members} TOTAL</span>
                                        {canWrite && (
                                            <button
                                                className="btn btn-link text-primary fw-bold text-decoration-none p-0 d-flex align-items-center gap-1"
                                                onClick={(e) => { e.preventDefault(); openAddUserModal(idx); }}
                                                style={{ fontSize: "14px" }}
                                            >
                                                +User <i className="bi bi-chevron-down" style={{ fontSize: "11px", WebkitTextStroke: "1px" }}></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="w-100" style={{ overflow: 'visible' }}>
                                    <table className="table wm-modern-table align-middle mb-0 w-100">
                                        <thead>
                                            <tr>
                                                <th>NAME</th>
                                                <th>ASSIGNED ROLE</th>
                                                <th>EMAIL</th>
                                                <th>STATUS</th>
                                                <th className="text-end pe-3">ACTIONS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentUsers.map((u, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="wm-avatar-round">
                                                                {getInitials(u.name)}
                                                            </div>
                                                            <div className="d-flex flex-column lh-1">
                                                                <span className="fw-bold text-dark mb-1" style={{ fontSize: "13px" }}>{u.name}</span>
                                                                <span className="text-muted" style={{ fontSize: "11px" }}>{u.email}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="wm-badge-role-pill">
                                                            {u.role.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted" style={{ fontSize: "13px" }}>{u.email}</td>
                                                    <td>
                                                        <span className="d-flex align-items-center gap-2" style={{ fontSize: "12px" }}>
                                                            <span className={`wm-status-dot ${u.status.toLowerCase() === 'active' ? 'bg-success' : 'bg-secondary'}`}></span>
                                                            <span className={u.status.toLowerCase() === 'active' ? 'text-success' : 'text-secondary'}>{u.status}</span>
                                                        </span>
                                                    </td>
                                                    <td className="text-end position-relative pe-3">
                                                        <i
                                                            className="bi bi-three-dots-vertical cursor-pointer text-muted fs-6 hover-primary p-2"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setShowUserMenu(showUserMenu === i ? null : i);
                                                            }}
                                                        ></i>

                                                        {showUserMenu === i && (
                                                            <>
                                                                <div className="wm-dropdown-backdrop" onClick={(e) => { e.stopPropagation(); setShowUserMenu(null); }}></div>
                                                                <div className="user-action-menu shadow-lg">
                                                                    <div className="menu-item" onClick={(e) => { e.stopPropagation(); setShowUserMenu(null); openAgentAccess(u); }}>
                                                                        Agent Access
                                                                    </div>
   <div className="menu-item" onClick={async (e) => {
    e.stopPropagation();
    setShowUserMenu(null);
    try {
        // ✅ BULLETPROOF FIX 1: Force fetch the absolute latest Master List of ALL agents!
        // This ensures a newly created agent is never "invisible" in the UI.
        const freshAgentsRes = await api.get(`/agents?t=${new Date().getTime()}`);
        setAgents(freshAgentsRes.data);

        // ✅ BULLETPROOF FIX 2: Fetch the absolute latest permissions for this user!
        const res = await api.get(`/workspace/user-agents/${u.email}?t=${new Date().getTime()}`);
        const agentData = res.data || [];
        
        setSelectedAgents(agentData.map(agent => String(agent.id)));
        
        const perms = {};
        agentData.forEach(a => {
            perms[a.id] = {
                history: a.can_view_history || false,
                docs: a.can_view_docs || false, 
                edit: a.can_edit || false
            };
        });
        setAgentPermissions(perms);
    } catch (err) {
        setSelectedAgents([]);
        setAgentPermissions({});
    }
    setUserForm({ name: u.name, email: u.email, role_id: u.role_id, status: u.status });
    setActiveWSIndex(idx);
    setActiveModal("editUser");
}}>
    Edit
</div>
                                                                    <div className="menu-item text-danger" onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setShowUserMenu(null);
                                                                        setUserToDelete({
                                                                            email: u.email,
                                                                            wsIdx: idx
                                                                        });
                                                                        setShowDeleteModal(true);
                                                                    }}>
                                                                        Delete
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {currentUsers.length === 0 && (
                                                <tr><td colSpan="5" className="text-center text-muted py-3">No users found.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ── REAL PAGINATION CONTROLS ── */}
                                {ws.userList.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3 text-muted" style={{ fontSize: "11px" }}>
                                        <span>Showing {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, ws.userList.length)} of {ws.members} employees</span>
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn btn-sm btn-outline-secondary border-0 bg-light px-2 py-0"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                            >
                                                <i className="bi bi-chevron-left" style={{ WebkitTextStroke: "1px" }}></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary border-0 bg-light px-2 py-0"
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}
                                            >
                                                <i className="bi bi-chevron-right" style={{ WebkitTextStroke: "1px" }}></i>
                                            </button>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── PRESERVED ADD/EDIT/ACCESS MODALS ── */}
            {(activeModal || agentsModal) && (
                <div className="wm-modal-overlay" onClick={() => { setActiveModal(null); setAgentsModal(false); }}>

                    {/* ADD WORKSPACE MODAL */}
                    {activeModal === 'addWS' && canWrite && (
                        <div className="wm-modal-content shadow-lg bg-white p-4" onClick={e => e.stopPropagation()}>
                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                <h5 className="fw-bold mb-0">Add Workspace</h5>
                                <i className="bi bi-x-lg cursor-pointer" onClick={() => setActiveModal(null)}></i>
                            </div>
                            <div className="text-start">
                                <div className="mb-3">
                                    <label className="form-label small text-muted">Workspace Name</label>
                                    <input type="text" className="form-control modal-input" placeholder="Finance" onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small text-muted">Industry</label>
                                    <select className="form-select modal-input custom-dropdown" style={dropdownStyle} onChange={(e) => setWsForm({ ...wsForm, industry: e.target.value })}>
                                        {industryList.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small text-muted">Department</label>
                                    <input type="text" className="form-control modal-input" placeholder="Finance" onChange={(e) => setWsForm({ ...wsForm, department: e.target.value })} />
                                </div>
                                <div className="mb-3">
                                    <p className="fw-bold mb-2">Add members :</p>
                                    <label className="form-label small text-muted">Invite via Email</label>
                                    <div className="d-flex gap-2 mb-2">
                                        <input type="email" className="form-control modal-input flex-grow-1" placeholder="Enter Email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                                        <button className="btn btn-primary px-4" onClick={handleAddEmail}>Add</button>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {tempEmails.map((email, i) => (
                                            <div key={i} className="email-tag d-inline-flex align-items-center gap-2 border rounded-pill px-3 py-1 small">
                                                {email} <i className="bi bi-x cursor-pointer" onClick={() => setTempEmails(tempEmails.filter(e => e !== email))}></i>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex gap-3 mt-4">
                                <button className="btn btn-outline-custom flex-grow-1" onClick={() => setActiveModal(null)}>Cancel</button>
                                <button className="btn btn-primary flex-grow-1" onClick={handleCreateWorkspace}>Create Workspace</button>
                            </div>
                        </div>
                    )}

                    {/* EDIT WORKSPACE MODAL */}
                    {activeModal === 'editWS' && canWrite && (
                        <div className="wm-modal-content shadow-lg bg-white p-4" onClick={e => e.stopPropagation()}>
                            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                                <h5 className="fw-bold mb-0">Edit Workspace</h5>
                                <i className="bi bi-x-lg cursor-pointer" onClick={() => setActiveModal(null)}></i>
                            </div>
                            <div className="text-start">
                                <div className="mb-3"><label className="form-label small text-muted">Workspace Name</label><input type="text" className="form-control modal-input" value={wsForm.name} onChange={(e) => setWsForm({ ...wsForm, name: e.target.value })} /></div>
                                <div className="mb-3">
                                    <label className="form-label small text-muted">Industry</label>
                                    <select className="form-select modal-input custom-dropdown" style={dropdownStyle} value={wsForm.industry} onChange={(e) => setWsForm({ ...wsForm, industry: e.target.value })}>
                                        {industryList.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                                    </select>
                                </div>
                                <div className="mb-4"><label className="form-label small text-muted">Department</label><input type="text" className="form-control modal-input" value={wsForm.department} onChange={(e) => setWsForm({ ...wsForm, department: e.target.value })} /></div>
                            </div>
                            <div className="d-flex justify-content-end gap-2">
                                <button className="btn btn-link text-decoration-none button-out" onClick={() => setActiveModal(null)}>Cancel</button>
                                <button className="btn btn-primary px-4" onClick={handleUpdateWorkspace}>Save</button>
                            </div>
                        </div>
                    )}

                    {/* ADD USER MODAL (COMPACT) */}
                    {activeModal === 'addUser' && canWrite && (
                        <div className="wm-modal-content shadow-lg bg-white p-3" onClick={e => e.stopPropagation()}>
                            <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-1">
                                <h6 className="fw-bold mb-0">Add User</h6>
                                <i className="bi bi-x-lg cursor-pointer" onClick={() => setActiveModal(null)}></i>
                            </div>
                            <div className="text-start">
                                <div className="mb-2 position-relative">
                                    <label className="form-label text-muted mb-1" style={{ fontSize: "11px" }}>Name</label>
                                    {showNamePopup && <div style={popupStyle}>Max 20 characters only</div>}
                                    <input type="text" className="form-control form-control-sm modal-input" placeholder="Enter Name" maxLength="20" onFocus={() => setShowNamePopup(true)} onBlur={() => setShowNamePopup(false)} value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} />
                                </div>
                                <div className="mb-2 position-relative">
                                    <label className="form-label text-muted mb-1" style={{ fontSize: "11px" }}>Email</label>
                                    {showEmailPopup && <div style={popupStyle}>Max 40 characters only</div>}
                                    <input type="email" className="form-control form-control-sm modal-input" placeholder="Enter Email" maxLength="40" onFocus={() => setShowEmailPopup(true)} onBlur={() => setShowEmailPopup(false)} value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
                                </div>
                                <div className="row mb-2">
                                    <div className="col-6">
                                        <label className="form-label text-muted mb-1" style={{ fontSize: "11px" }}>Role</label>
                                        <select className="form-select form-select-sm modal-input custom-dropdown" value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                                            <option value="">Select Role</option>
                                            {availableRoles.map(role => <option key={role.role_id} value={role.role_id}>{role.role_name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label text-muted mb-1" style={{ fontSize: "11px" }}>Status</label>
                                        <select className="form-select form-select-sm modal-input custom-dropdown" value={userForm.status} onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}>
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: "11px" }}>Agents Access & Permissions</label>
                                    <div className="border rounded p-1" style={{ maxHeight: "130px", overflowY: "auto" }}>
                                        {agents.map(agent => {
                                            const isSelected = selectedAgents.includes(String(agent.id));
                                            const isActive = parseInt(agent.doc_count) > 0;
                                            return (
                                                <div key={agent.id} className="mb-1 border-bottom pb-1">
                                                    <div className="form-check d-flex align-items-center mb-0">
                                                        <input className="form-check-input mt-0" type="checkbox" value={agent.id} checked={isSelected}onChange={(e) => {
    const val = e.target.value;
    if (e.target.checked) {
        setSelectedAgents([...selectedAgents, val]);
        // ✅ FIX: Default history and docs to TRUE so the junior can actually see them!
        setAgentPermissions(prev => ({ ...prev, [val]: { history: true, docs: true, edit: false } }));
    } else {
        setSelectedAgents(selectedAgents.filter(a => a !== val));
        const newP = { ...agentPermissions }; delete newP[val]; setAgentPermissions(newP);
    }
}} />
                                                        <label className="form-check-label ms-2 fw-medium" style={{ fontSize: "11px" }}>{agent.name}</label>
                                                    </div>
                                                    {isSelected && (
                                                        <div className="ms-4 mt-1 d-flex gap-3 flex-wrap">
                                                            {/* History checkbox - ONLY for active agents */}
                                                            {isActive && (
                                                                <div className="form-check mb-0 d-flex align-items-center gap-1">
                                                                    <input type="checkbox" className="form-check-input mt-0" style={{ transform: 'scale(0.8)' }} checked={agentPermissions[agent.id]?.history || false} onChange={(e) => setAgentPermissions(prev => ({ ...prev, [agent.id]: { ...prev[agent.id], history: e.target.checked } }))} />
                                                                    <label className="text-muted mb-0" style={{ fontSize: '10px' }}>History</label>
                                                                </div>
                                                            )}
                                                            {/* Edit checkbox - for BOTH draft and active agents */}
                                                            <div className="form-check mb-0 d-flex align-items-center gap-1">
                                                                <input type="checkbox" className="form-check-input mt-0" style={{ transform: 'scale(0.8)' }} checked={agentPermissions[agent.id]?.edit || false} onChange={(e) => setAgentPermissions(prev => ({ ...prev, [agent.id]: { ...prev[agent.id], edit: e.target.checked } }))} />
                                                                <label className="text-muted mb-0" style={{ fontSize: '10px' }}>Edit</label>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-outline-primary flex-grow-1 fw-bold" onClick={() => setActiveModal(null)}>Cancel</button>
                                <button className="btn btn-sm btn-primary flex-grow-1 fw-bold" onClick={handleAddNewUser} disabled={isInviting || !userForm.name || !userForm.email || !userForm.role}>
                                    {isInviting ? "Sending..." : "Add User"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* EDIT USER MODAL (COMPACT) */}
                    {activeModal === 'editUser' && canWrite && (
                        <div className="wm-modal-content shadow-lg bg-white p-3" onClick={e => e.stopPropagation()}>
                            <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-1">
                                <h6 className="fw-bold mb-0">Edit User</h6>
                                <i className="bi bi-x-lg cursor-pointer" onClick={() => setActiveModal(null)}></i>
                            </div>
                            <div className="mb-2 text-start">
                                <label className="form-label text-muted mb-1" style={{ fontSize: "11px" }}>Name</label>
                                <input type="text" className="form-control form-control-sm" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} />
                            </div>
                            <div className="mb-2 text-start">
                                <label className="form-label text-muted mb-1" style={{ fontSize: "11px" }}>Email</label>
                                <input type="email" className="form-control form-control-sm" value={userForm.email} disabled />
                            </div>
                            <div className="row mb-2">
                                <div className="col-6 text-start">
                                    <label className="form-label text-muted mb-1" style={{ fontSize: "11px" }}>Role</label>
                                    <select className="form-select form-select-sm" value={userForm.role_id || ""} onChange={(e) => setUserForm({ ...userForm, role_id: e.target.value })}>
                                        <option value="">Select Role</option>
                                        {availableRoles.map(role => <option key={role.role_id} value={role.role_id}>{role.role_name}</option>)}
                                    </select>
                                </div>
                                <div className="col-6 text-start">
                                    <label className="form-label text-muted mb-1" style={{ fontSize: "11px" }}>Status</label>
                                    <select className="form-select form-select-sm" value={userForm.status} onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mb-3 text-start">
                                <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: "11px" }}>Agents Access & Permissions</label>
                                <div className="border rounded p-1" style={{ maxHeight: "130px", overflowY: "auto" }}>
                                    {agents.map(agent => {
                                        const isSelected = selectedAgents.includes(String(agent.id));
                                        const isActive = parseInt(agent.doc_count) > 0;
                                        return (
                                            <div key={agent.id} className="mb-1 border-bottom pb-1">
                                                <div className="form-check d-flex align-items-center mb-0">
                                                    <input className="form-check-input mt-0" type="checkbox" value={agent.id} checked={isSelected} onChange={(e) => {
    const val = e.target.value;
    if (e.target.checked) {
        setSelectedAgents([...selectedAgents, val]);
        // ✅ FIX: Default history and docs to TRUE so the junior can actually see them!
        setAgentPermissions(prev => ({ ...prev, [val]: { history: true, docs: true, edit: false } }));
    } else {
        setSelectedAgents(selectedAgents.filter(a => a !== val));
        const newP = { ...agentPermissions }; delete newP[val]; setAgentPermissions(newP);
    }
}} />
                                                    <label className="form-check-label ms-2 fw-medium" style={{ fontSize: "11px" }}>{agent.name}</label>
                                                </div>
                                                {isSelected && (
                                                    <div className="ms-4 mt-1 d-flex gap-3 flex-wrap">
                                                        {/* History checkbox - ONLY for active agents */}
                                                        {isActive && (
                                                            <div className="form-check mb-0 d-flex align-items-center gap-1">
                                                                <input type="checkbox" className="form-check-input mt-0" style={{ transform: 'scale(0.8)' }} checked={agentPermissions[agent.id]?.history || false} onChange={(e) => setAgentPermissions(prev => ({ ...prev, [agent.id]: { ...prev[agent.id], history: e.target.checked } }))} />
                                                                <label className="text-muted mb-0" style={{ fontSize: '10px' }}>History</label>
                                                            </div>
                                                        )}
                                                        {/* Edit checkbox - for BOTH draft and active agents */}
                                                        <div className="form-check mb-0 d-flex align-items-center gap-1">
                                                            <input type="checkbox" className="form-check-input mt-0" style={{ transform: 'scale(0.8)' }} checked={agentPermissions[agent.id]?.edit || false} onChange={(e) => setAgentPermissions(prev => ({ ...prev, [agent.id]: { ...prev[agent.id], edit: e.target.checked } }))} />
                                                            <label className="text-muted mb-0" style={{ fontSize: '10px' }}>Edit</label>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-outline-secondary flex-grow-1 fw-bold" onClick={() => setActiveModal(null)}>Cancel</button>
                                <button className="btn btn-sm btn-primary flex-grow-1 fw-bold" onClick={handleUpdateUser}>Update User</button>
                            </div>
                        </div>
                    )}

                    {/* AGENT ACCESS MODAL (COMPACT) */}
                    {agentsModal && (
                        <div className="wm-modal-content shadow-lg bg-white p-3" onClick={e => e.stopPropagation()}>
                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                <h6 className="fw-bold mb-0 text-dark">Agent Access</h6>
                                <i className="bi bi-x-lg cursor-pointer text-muted" onClick={() => setAgentsModal(false)}></i>
                            </div>

                            <div className="text-start mb-3">
                                <p className="text-muted mb-0" style={{ fontSize: "11px" }}>User</p>
                                <p className="fw-bold text-dark mb-0" style={{ fontSize: "13px" }}>{selectedUser?.name}</p>
                            </div>

                            {userAgents.length === 0 ? (
                                <div className="text-center text-muted py-3" style={{ fontSize: "12px" }}>No agents assigned</div>
                            ) : (
                                <div className="list-group" style={{ maxHeight: "250px", overflowY: "auto" }}>
                                    {userAgents.map(agent => (
                                        <div key={agent.id} className="list-group-item d-flex justify-content-between align-items-center py-2 px-3">
                                            <span className="fw-medium text-dark text-truncate pe-2" style={{ fontSize: "12px", maxWidth: "60%" }}>
                                                {agent.name}
                                            </span>
                                         <div className="d-flex gap-1 align-items-center">
    {agent.can_view_history && (
        <span className="badge bg-info-subtle text-info" style={{ fontSize: '9px', padding: '4px 6px' }}>History</span>
    )}
    {/* ✅ FIX: Added the missing Docs badge so you can see it in the UI */}
    {agent.can_view_docs && (
        <span className="badge bg-primary-subtle text-primary" style={{ fontSize: '9px', padding: '4px 6px' }}>Docs</span>
    )}
    {agent.can_edit && (
        <span className="badge bg-warning-subtle text-warning" style={{ fontSize: '9px', padding: '4px 6px' }}>Edit</span>
    )}
    <span className="badge bg-success-subtle text-success" style={{ fontSize: '9px', padding: '4px 6px' }}>Access</span>
</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            {showDeleteModal && (
                <div
                    className="modal fade show d-block"
                    onClick={() => {
                        setShowDeleteModal(false);
                        setUserToDelete(null);
                    }}
                    style={{
                        background: "rgba(0,0,0,0.45)",
                        zIndex: 9999,
                    }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div
                            className="modal-content border-0"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                borderRadius: "18px",
                                overflow: "hidden",
                            }}
                        >
                            <div className="modal-body text-center p-4">
                                <div
                                    style={{
                                        width: "68px",
                                        height: "68px",
                                        background: "#FEECEC",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        margin: "0 auto 18px",
                                    }}
                                >
                                    <i
                                        className="bi bi-trash3"
                                        style={{
                                            color: "#ef4444",
                                            fontSize: "28px",
                                        }}
                                    ></i>
                                </div>
                                <h5
                                    style={{
                                        fontWeight: 700,
                                        marginBottom: "10px",
                                    }}
                                >
                                    Delete User?
                                </h5>
                                <p
                                    style={{
                                        color: "#6b7280",
                                        fontSize: "14px",
                                        marginBottom: "26px",
                                    }}
                                >
                                    This action cannot be undone.
                                </p>
                                <div className="d-flex justify-content-center gap-3">
                                    <button
                                        className="btn btn-light px-4"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setUserToDelete(null);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-danger px-4"
                                        onClick={handleDeleteUser}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkSpaceManagement;